/**
 * [글자색·형광펜 메뉴]
 * - 역할: Notion 스타일 팔레트 UI (최근 사용 / 글자 색 / 강조 색)
 * - 작성/수정일: 2026-08-12
 * - 수정일: 2026-08-12 — 한국어 라벨 · 툴바 팔레트 아이콘
 */
import { iconSvg, paletteIconSvg } from '../icons.js';
import {
  TEXT_COLOR_PRESETS,
  HIGHLIGHT_COLOR_PRESETS,
  applyTextColor,
  applyHighlightColor,
  getActiveColors,
  loadRecentColors,
} from '../color-palette.js';

function createSwatchButton({ kind, value, swatch, label, active, onPick }) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = `g5-gurumi-color-swatch g5-gurumi-color-swatch--${kind}${active ? ' is-active' : ''}${!value ? ' is-default' : ''}`;
  btn.setAttribute('aria-label', label);
  btn.title = label;
  btn.setAttribute('aria-pressed', active ? 'true' : 'false');

  if (kind === 'text') {
    btn.classList.add('g5-gurumi-color-swatch--letter');
    const tone = swatch || '#37352F';
    btn.style.borderColor = tone;
    const letter = document.createElement('span');
    letter.className = 'g5-gurumi-color-swatch-letter';
    letter.textContent = 'A';
    letter.style.color = tone;
    btn.appendChild(letter);
  } else {
    btn.classList.add('g5-gurumi-color-swatch--fill');
    btn.style.backgroundColor = swatch || '#FFFFFF';
    if (!value) {
      btn.style.borderColor = '#E3E2E0';
      btn.style.backgroundColor = '#FFFFFF';
    } else {
      btn.style.borderColor = 'transparent';
    }
  }

  btn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    onPick();
  });
  return btn;
}

export function createColorMenu(editor, onStateChange) {
  const root = document.createElement('div');
  root.className = 'g5-gurumi-color-menu';

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'g5-gurumi-tool-btn g5-gurumi-color-btn';
  btn.setAttribute('aria-haspopup', 'menu');
  btn.setAttribute('aria-label', '글자 색 · 강조 색');
  btn.setAttribute('aria-expanded', 'false');

  const iconWrap = document.createElement('span');
  iconWrap.className = 'g5-gurumi-color-btn-icon';
  iconWrap.setAttribute('aria-hidden', 'true');
  iconWrap.appendChild(paletteIconSvg());
  btn.appendChild(iconWrap);
  btn.appendChild(iconSvg('chevronDown', 'g5-gurumi-icon g5-gurumi-icon-muted'));

  const panel = document.createElement('div');
  panel.className = 'g5-gurumi-color-dropdown';
  panel.setAttribute('role', 'menu');
  panel.hidden = true;

  const recentSection = document.createElement('div');
  recentSection.className = 'g5-gurumi-color-section';
  recentSection.dataset.section = 'recent';
  const recentTitle = document.createElement('div');
  recentTitle.className = 'g5-gurumi-color-section-title';
  recentTitle.textContent = '최근 사용';
  const recentGrid = document.createElement('div');
  recentGrid.className = 'g5-gurumi-color-grid g5-gurumi-color-grid--recent';
  recentSection.appendChild(recentTitle);
  recentSection.appendChild(recentGrid);

  function buildPresetSection(title, presets, kind) {
    const section = document.createElement('div');
    section.className = 'g5-gurumi-color-section';
    const h = document.createElement('div');
    h.className = 'g5-gurumi-color-section-title';
    h.textContent = title;
    const grid = document.createElement('div');
    grid.className = 'g5-gurumi-color-grid';
    grid.dataset.kind = kind;
    presets.forEach((preset) => {
      grid.appendChild(
        createSwatchButton({
          kind,
          value: preset.value,
          swatch: preset.swatch,
          label: preset.label,
          active: false,
          onPick: () => {
            if (kind === 'text') applyTextColor(editor, preset.value);
            else applyHighlightColor(editor, preset.value);
            close();
            refresh();
            onStateChange?.();
          },
        }),
      );
    });
    section.appendChild(h);
    section.appendChild(grid);
    return { section, grid };
  }

  const textBuilt = buildPresetSection('글자 색', TEXT_COLOR_PRESETS, 'text');
  const highlightBuilt = buildPresetSection('강조 색', HIGHLIGHT_COLOR_PRESETS, 'highlight');

  panel.appendChild(recentSection);
  panel.appendChild(textBuilt.section);
  panel.appendChild(highlightBuilt.section);

  let openState = false;

  function syncPresetActive(grid, kind, activeValue) {
    const buttons = grid.querySelectorAll('.g5-gurumi-color-swatch');
    buttons.forEach((el, idx) => {
      const presets = kind === 'text' ? TEXT_COLOR_PRESETS : HIGHLIGHT_COLOR_PRESETS;
      const preset = presets[idx];
      if (!preset) return;
      const selected = (preset.value || null) === (activeValue || null);
      el.classList.toggle('is-active', selected);
      el.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
  }

  function renderRecent(active) {
    recentGrid.innerHTML = '';
    const recent = loadRecentColors();
    if (!recent.length) {
      recentSection.hidden = true;
      return;
    }
    recentSection.hidden = false;
    recent.forEach((item) => {
      const presetList = item.kind === 'text' ? TEXT_COLOR_PRESETS : HIGHLIGHT_COLOR_PRESETS;
      const found = presetList.find((p) => p.value === item.value);
      const swatch = found ? found.swatch : item.value;
      const label = found ? found.label : item.value;
      const isActive =
        item.kind === 'text'
          ? (active.text || null) === item.value
          : (active.highlight || null) === item.value;
      recentGrid.appendChild(
        createSwatchButton({
          kind: item.kind,
          value: item.value,
          swatch,
          label: `최근 · ${label}`,
          active: isActive,
          onPick: () => {
            if (item.kind === 'text') applyTextColor(editor, item.value);
            else applyHighlightColor(editor, item.value);
            close();
            refresh();
            onStateChange?.();
          },
        }),
      );
    });
  }

  function refresh() {
    const active = getActiveColors(editor);
    // 글자색 우선, 없으면 강조색으로 단색 칩 표시 — 작성/수정일: 2026-08-12
    const chipColor = active.text || active.highlight || '';
    iconWrap.innerHTML = '';
    iconWrap.appendChild(paletteIconSvg(chipColor));
    btn.classList.toggle('has-color', !!(active.text || active.highlight));
    renderRecent(active);
    syncPresetActive(textBuilt.grid, 'text', active.text);
    syncPresetActive(highlightBuilt.grid, 'highlight', active.highlight);
    if (openState) positionPanel();
  }

  /**
   * [글자색·형광펜]
   * - 역할: 툴바 overflow 클리핑 회피 — body + fixed
   * - 작성/수정일: 2026-08-12
   */
  function positionPanel() {
    const rect = btn.getBoundingClientRect();
    const gap = 8;
    const width = Math.min(260, window.innerWidth - 16);
    let left = rect.left;
    if (left + width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - width - 8);
    }
    panel.style.position = 'fixed';
    panel.style.left = `${Math.round(left)}px`;
    panel.style.top = `${Math.round(rect.bottom + gap)}px`;
    panel.style.width = `${width}px`;
    panel.style.right = 'auto';
    panel.style.zIndex = '10050';

    requestAnimationFrame(() => {
      const pr = panel.getBoundingClientRect();
      if (pr.bottom > window.innerHeight - 8) {
        const above = rect.top - pr.height - gap;
        if (above >= 8) panel.style.top = `${Math.round(above)}px`;
      }
    });
  }

  function open() {
    refresh();
    openState = true;
    panel.hidden = false;
    btn.classList.add('is-open');
    btn.setAttribute('aria-expanded', 'true');
    if (panel.parentElement !== document.body) {
      document.body.appendChild(panel);
    }
    positionPanel();
  }

  function close() {
    if (!openState && panel.hidden) return;
    openState = false;
    panel.hidden = true;
    btn.classList.remove('is-open');
    btn.setAttribute('aria-expanded', 'false');
    if (panel.parentElement === document.body) {
      root.appendChild(panel);
    }
  }

  btn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (openState) close();
    else open();
  });
  btn.addEventListener('mousedown', (e) => e.stopPropagation());
  panel.addEventListener('mousedown', (e) => e.stopPropagation());

  document.addEventListener('mousedown', (e) => {
    if (!openState) return;
    if (root.contains(e.target) || panel.contains(e.target)) return;
    close();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });
  window.addEventListener(
    'scroll',
    () => {
      if (openState) positionPanel();
    },
    true,
  );
  window.addEventListener('resize', () => {
    if (openState) positionPanel();
  });

  root.appendChild(btn);
  root.appendChild(panel);

  return { root, refresh, close };
}
